// MoreSheet.tsx — THE REST OF THE APP, ON A PHONE (Huan, 2026-08-25:
// "Today - Clients - Knot - Chats - More. All the other screens can be put
// under the more which gives options when you click it.")
//
// WHY IT EXISTS. A bottom bar holds four things before the centred Knot stops
// being centred; the owner's rail declares eleven. The first answer was to
// pick the four the day is spent in and let Find reach the rest. That was
// wrong in a way worth writing down: Find is a SEARCH box, and you cannot
// search for a screen whose name you have not learned yet. "Not hidden" and
// "findable" are different promises, and only the second one is any use to
// someone opening the app for the first time.
//
// So this is a door, not a search. It lists what the bar leaves out, by name.
//
// It is deliberately NOT a new surface archetype: same fixed/scrim/box/
// role="dialog"/Escape construction as FindSheet, which is the settled shape
// for "something rose over the app and will go away again".
import { useEffect, useRef } from 'preact/hooks';
import { navHref, isGoldTab, badgeLabel, type NavDestination, type TabBadge } from './navModel';
import type { TopTab } from '../routes/route';
import './MoreSheet.css';

function groupRuns(destinations: NavDestination[]): { key: string; label: string; items: NavDestination[] }[] {
  const groups: { key: string; label: string; items: NavDestination[] }[] = [];
  for (const item of destinations) {
    const key = item.group ?? '';
    const last = groups[groups.length - 1];
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, label: item.groupLabel ?? '', items: [item] });
  }
  // Headings only when the pack actually named its groups — a lone unlabelled
  // run is the flat list, not a group with a blank title.
  if (!groups.some((g) => g.label.length > 0)) for (const g of groups) g.label = '';
  return groups;
}

/** B8 — an entry that is not one of the rail's own tabs (no `TopTab`, no
 * server-declared NavDestination) but still belongs inside this sheet by
 * name: "Your page" (Huan: "More → Your page"). Its own trailing, unlabelled
 * group, separate from the pack's declared groups above it — the smallest
 * way to add a door here without teaching this file a second destination
 * model. */
export interface MoreSheetExtraEntry {
  key: string;
  label: string;
  href: string;
  onNavigate: () => void;
}

export interface MoreSheetProps {
  destinations: NavDestination[];
  active: TopTab;
  onNavigate: (tab: TopTab) => void;
  onClose: () => void;
  badges?: Partial<Record<TopTab, TabBadge>>;
  extra?: MoreSheetExtraEntry[];
}

export function MoreSheet({ destinations, active, onNavigate, onClose, badges, extra }: MoreSheetProps) {
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    }
    window.addEventListener('keydown', onKeyDown);
    // Focus lands on the first destination, so a keyboard or screen-reader
    // user is IN the list rather than hunting for it.
    const first = boxRef.current?.querySelector('a');
    if (first instanceof HTMLAnchorElement) first.focus();
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div class="more-sheet" data-testid="more-sheet">
      <button type="button" class="more-sheet__scrim" aria-label="Close" onClick={onClose} />
      <div class="more-sheet__box" ref={boxRef} role="dialog" aria-label="More screens">
        <div class="more-sheet__grip" aria-hidden="true" />
        <div class="more-sheet__list">
          {/* A3, carried across the merge: grouped in DECLARATION ORDER by
              the pack's own labels — consecutive runs, never a sort, the same
              way SideRail builds its groups, so the two renderings cannot
              disagree. A pack that declares no groups (the cockpit) renders
              exactly the flat list this file had before the graft. */}
          {groupRuns(destinations).map((g) => (
            <div class="more-sheet__group" key={g.key}>
              {g.label ? <div class="more-sheet__grouplab">{g.label}</div> : null}
              {g.items.map((item) => {
            const count = badgeLabel(badges?.[item.tab]);
            const classes = ['more-sheet__item'];
            if (active === item.tab) {
              classes.push(isGoldTab(item.tab) ? 'more-sheet__item--gold' : 'more-sheet__item--active');
            }
            return (
              <a
                key={item.tab}
                class={classes.join(' ')}
                href={navHref(item.tab)}
                aria-current={active === item.tab ? 'page' : undefined}
                onClick={(e) => {
                  e.preventDefault();
                  onNavigate(item.tab);
                  // Closing is the caller's business on every other sheet in
                  // this app; here the navigation IS the dismissal, so doing
                  // it in one place keeps the caller from having to know.
                  onClose();
                }}
              >
                <span class="more-sheet__dot" />
                {/* The FULL label, not the bar's `short`. The bar abbreviates
                    because it is 60px wide; a list has room to say the real
                    name, which is the name the person has to learn. */}
                <span class="more-sheet__name">{item.label}</span>
                {count !== null ? <span class="more-sheet__badge">{count}</span> : null}
              </a>
            );
              })}
            </div>
          ))}
          {extra && extra.length > 0 ? (
            <div class="more-sheet__group" key="extra">
              {extra.map((item) => (
                <a
                  key={item.key}
                  class="more-sheet__item"
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    item.onNavigate();
                    onClose();
                  }}
                >
                  <span class="more-sheet__dot" />
                  <span class="more-sheet__name">{item.label}</span>
                </a>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
